import DoospLayout from "@/components/doosp/DoospLayout";
import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Tag } from "lucide-react";

const ARTICLES = [
  {
    slug: "needlestick-injuries-2026",
    title: "The evolving landscape of needlestick injuries in ambulatory settings",
    excerpt:
      "Outpatient and ambulatory clinics now account for a growing share of reported needlestick exposures. We look at the drivers and what teams can do differently.",
    author: "Dr. Priya Rao",
    date: "2026-01-15",
    tags: ["Bloodborne Pathogens", "Research"],
    image: "https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=1200&q=80",
  },
  {
    slug: "ppe-fit-testing",
    title: "Why PPE fit testing matters more than PPE selection",
    excerpt:
      "The most expensive N95 in the world is useless if it doesn&rsquo;t seal. A practical guide to fit-testing programs that actually get done.",
    author: "Marcus Chen, MPH",
    date: "2026-01-08",
    tags: ["PPE", "Infection Control"],
    image: "https://images.unsplash.com/photo-1584515933487-779824d29309?w=1200&q=80",
  },
  {
    slug: "interpreting-exposure-report",
    title: "Interpreting your BBP exposure incident report",
    excerpt:
      "A walkthrough of the six data fields that actually predict follow-up compliance — and the two that most incident reports miss entirely.",
    author: "Elena Vasquez, RN, COHN",
    date: "2025-12-20",
    tags: ["Bloodborne Pathogens", "Compliance"],
    image: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1200&q=80",
  },
  {
    slug: "infection-control-case-studies",
    title: "Case studies: infection control failures and lessons learned",
    excerpt:
      "Three anonymized outbreak reviews from 2024–2025, and the systemic patterns they reveal about environmental cleaning schedules.",
    author: "Dr. Aditi Sharma",
    date: "2025-12-02",
    tags: ["Infection Control", "Case Study"],
    image: "https://images.unsplash.com/photo-1584467735867-4297ae2ebcee?w=1200&q=80",
  },
  {
    slug: "laser-safety-officer-role",
    title: "Building a laser safety officer role in a mid-size clinic",
    excerpt:
      "Not every organization needs a full-time LSO — but every organization needs someone accountable. A model for shared responsibility.",
    author: "Jonathan Park, MD",
    date: "2025-11-18",
    tags: ["Laser Safety"],
    image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&q=80",
  },
  {
    slug: "waste-segregation-audits",
    title: "Waste segregation audits: what the top-performing hospitals do",
    excerpt:
      "A comparison of medical waste segregation practices across 40 hospitals. The best-performing sites share three habits — none of them expensive.",
    author: "Sarah Okonkwo, MSc",
    date: "2025-10-30",
    tags: ["Waste Management"],
    image: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1200&q=80",
  },
];

export default function News() {
  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">News &amp; articles</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="news-heading">
            Analysis and insight from<br />
            <span className="text-red-800">the field.</span>
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-3xl leading-relaxed">
            Practitioner-authored writing on infection control, occupational safety, and
            the evolving evidence base behind our certification programs.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          {/* Featured */}
          <article className="grid md:grid-cols-12 gap-8 items-center mb-16">
            <div className="md:col-span-7">
              <div className="rounded-2xl overflow-hidden border border-zinc-200">
                <img src={ARTICLES[0].image} alt="" className="w-full h-72 md:h-96 object-cover" />
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="text-xs font-mono uppercase tracking-widest text-red-800">Featured</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 leading-tight">
                {ARTICLES[0].title}
              </h2>
              <p className="text-zinc-600 mt-4 leading-relaxed">{ARTICLES[0].excerpt}</p>
              <div className="flex items-center gap-3 mt-5 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                <User className="h-3 w-3" /> {ARTICLES[0].author}
                <span>·</span>
                <Calendar className="h-3 w-3" /> {ARTICLES[0].date}
              </div>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-red-700 hover:text-red-900 font-medium text-sm">
                Read on the DOOSP journal <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </article>

          {/* Rest */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTICLES.slice(1).map((a) => (
              <article key={a.slug} className="border border-zinc-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition group" data-testid={`article-${a.slug}`}>
                <div className="overflow-hidden">
                  <img src={a.image} alt="" className="w-full h-40 object-cover group-hover:scale-105 transition duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {a.tags.map((t) => (
                      <span key={t} className="text-[10px] font-mono uppercase tracking-widest text-red-800 bg-red-50 border border-red-100 px-2 py-0.5 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-display font-bold text-lg leading-tight group-hover:text-red-800 transition">{a.title}</h3>
                  <p className="text-sm text-zinc-600 mt-2 line-clamp-3 leading-relaxed">{a.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-[11px] font-mono uppercase text-zinc-500">
                    <span>{a.date}</span>
                    <span className="text-red-700">Read →</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Have a story to pitch?</h2>
          <p className="text-zinc-400 mt-3">
            The DOOSP journal accepts practitioner-authored articles on occupational safety and infection control.
          </p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white h-11 px-6 rounded-md text-sm font-medium">
            Pitch us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </DoospLayout>
  );
}
